import { ArrowRightIcon, Note, SaveButton, SecondaryButton } from '@movingimage-evp/mi-ui-component-library';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation, useParams } from 'react-router';

import { State, useGetWebcastStateQuery } from '../../../generated/graphql-manager';
import { useAbsoluteRoutes } from '../../../routes';
import { addParamToPath } from '../../../utils';
import { useUserPermissions } from '../../hooks/user-permissions';

import styles from './setup-page-footer.module.css';
import managerStyles from '../../manager.module.css';

type SetupPageFooterProps = {
  nextLabel: string;
  route: string;
  saving?: boolean;
  showSaveButton?: boolean;
  saveButtonLabel?: string;
  saveButtonDisabled?: boolean;
  nextButtonDisabled?: boolean;
  savingFailed?: boolean;
  onSaveButtonClick?: () => void;
};

export function SetupPageFooter({
  nextLabel,
  route,
  showSaveButton,
  saveButtonLabel,
  saveButtonDisabled,
  nextButtonDisabled,
  saving,
  savingFailed,
  onSaveButtonClick,
}: SetupPageFooterProps) {
  const { webcastId = '' } = useParams();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { isEventEditingAllowed } = useUserPermissions();
  const routes = useAbsoluteRoutes();

  const webcastStateResponse = useGetWebcastStateQuery({ variables: { webcastId }, skip: !webcastId });
  const webcastState = webcastStateResponse.data?.webcast?.state;

  const isLiveTestState = webcastState && webcastState === State.LIVE_TEST;
  const isPostLiveState = webcastState && webcastState === State.POSTLIVE;
  const isArchivedState = webcastState && webcastState === State.ARCHIVED;

  const isEventSetup = pathname.includes(routes.webcastSetup_eventDetails);
  const isEventDescriptionPage = pathname.includes(routes.webcastSetup_eventDescription);
  const isEventPermissionsPage = pathname.includes(routes.webcastSetup_eventPermissions);
  const isMediaPage = pathname.includes(routes.webcastSetup_media);
  const isMessagingPage = pathname.includes(routes.webcastSetup_messaging);
  const isEventAccessPage = pathname.includes(routes.webcastSetup_eventAccess);
  const isMessagingPageEditable = isMessagingPage && !isPostLiveState;

  const editablePartOfWebcast =
    isEventSetup ||
    isEventDescriptionPage ||
    isMediaPage ||
    isEventPermissionsPage ||
    isMessagingPageEditable ||
    isEventAccessPage;

  let infoBoxText = '';

  if (!editablePartOfWebcast && isLiveTestState) infoBoxText = t('manager.webcastSetup.notEditableMessage.liveTest');
  if (!editablePartOfWebcast && isPostLiveState) infoBoxText = t('manager.webcastSetup.notEditableMessage.postLive');
  if (isArchivedState) infoBoxText = t('manager.webcastSetup.notEditableMessage.archived');
  if (!isEventEditingAllowed) infoBoxText = t('manager.webcastSetup.notEditableMessage.noPermission');

  return (
    <>
      <div className={managerStyles.buttons}>
        {showSaveButton && (
          <SaveButton
            data-testid="submit-button"
            form="event-details-form"
            disabled={saveButtonDisabled}
            saving={saving}
            savingFailed={savingFailed}
            onClick={onSaveButtonClick}
            timeout={3000}
            saveLabel={saveButtonLabel}
          />
        )}

        <SecondaryButton
          type="button"
          component={NavLink}
          to={addParamToPath(route, webcastId)}
          data-testid="next-step-button"
          disabled={nextButtonDisabled}
        >
          {nextLabel}
          <ArrowRightIcon />
        </SecondaryButton>
      </div>

      {infoBoxText && (
        <Note variant="primary" className={styles.infoBox} data-testid="infobox">
          {infoBoxText}
        </Note>
      )}
    </>
  );
}
